/**
 * 汎用 classifications テーブルレンダラ (列名動的化レベル 3、docs/105 / old2 移植)。
 *
 * old2 `apps/user/src/lib/classificationTableRenderer.tsx` の逐語移植。
 * `AiOutputFormatSchema.fields[]` の `object-array` の `itemFields[]` から
 * テーブルの列構築・セル描画を行い、mode 別ハードコード (#/評価/対象物/連鎖/コメント 等の
 * 固定列名) を排除する。admin が fields[] で列を増減すれば詳細テーブルが追従する。
 *
 * old2 との差分 (v2 データモデル吸収):
 *   - 入力は old2 `AiOutputFormatMaster` ではなく v2 `AiOutputFormatSchema`
 *     (= `AiOutputFormat.schema`)。`normalizeAiOutputFormat` / `deriveClassificationItemFields`
 *     も v2 では schema を受けるため、こちらに合わせる。
 *   - old2 `assessment.positiveLabel` 等は v2 でも `schema.assessment` に同名で存在する。
 *
 * 視覚的にまとめたい次の 3 パターンだけはキーを見て自動グループ化 (old2 踏襲):
 *   1. `<prefix>_code` + `<prefix>_name` → 1 セル (code を小さく上、name を下)
 *   2. `assessment` → ポジ/ネガ配色バッジ (assessment_level / score は別列)
 *   3. それ以外は itemField 1 つ = 1 セル
 *
 * 配色は v2 preset の neutral/green/orange tone を使用 (動的色 inline style は使わない)。
 */
import type { ReactNode } from 'react';
import {
  normalizeAiOutputFormat,
  type AiOutputFieldDef,
  type AiOutputFormatSchema,
  type PostDetailVisibility,
} from '@citizen-walk/shared-types';

export interface ClassificationColumn {
  key: string;
  label: string;
  render: (row: Record<string, unknown>, rowIndex: number) => ReactNode;
}

/**
 * schema.fields[] から classifications テーブルの列定義を構築する。
 * fields に object-array が無ければ null を返す (= 呼び出し側は従来の簡易表示に fallback)。
 *
 * issue #24: `visibility` を渡すと、`classificationAxes === false` で分類軸 (各 axis の
 * code/name/subField) 列を、`assessment === false` で評価ラベル (assessment / assessment_level /
 * score) 列を除外する。未指定 (従来呼び出し) は全列を出す (後方互換)。
 */
export function buildClassificationColumns(
  schema: AiOutputFormatSchema,
  visibility?: PostDetailVisibility,
): ClassificationColumn[] | null {
  const norm = normalizeAiOutputFormat(schema);
  const objArr = norm.fields?.find((f) => f.kind === 'object-array');
  if (!objArr || !objArr.itemFields || objArr.itemFields.length === 0) return null;

  const itemFields = objArr.itemFields;
  const skip = new Set<string>();
  // issue #24: 可視性で除外する itemField キー。分類軸 off は axis 由来 (code/name/subField)、
  // 評価ラベル off は assessment 派生 (assessment / assessment_level / score) を落とす。
  const hidden = new Set<string>();
  if (visibility?.classificationAxes === false) {
    for (const ax of norm.classificationAxes ?? []) {
      if (ax.codeField) hidden.add(ax.codeField);
      if (ax.nameField) hidden.add(ax.nameField);
      if (ax.subField) hidden.add(ax.subField);
    }
  }
  if (visibility?.assessment === false) {
    hidden.add('assessment');
    hidden.add('assessment_level');
    hidden.add('score');
  }
  const columns: ClassificationColumn[] = [];
  const posLabel = norm.assessment?.positiveLabel ?? '';
  const negLabel = norm.assessment?.negativeLabel ?? '';

  for (const f of itemFields) {
    if (skip.has(f.key) || hidden.has(f.key)) continue;

    // assessment は単独列として描画 (ハードコード「評価」は廃止、label は itemField のものを使う)。
    // ポジ/ネガの色付けは維持。assessment_level と score は別列。
    if (f.key === 'assessment' && f.kind === 'text') {
      columns.push({
        key: f.key,
        label: f.label || f.key,
        render: (row) => {
          const a = String(row.assessment ?? '');
          if (!a) return <span className="text-slate-400">—</span>;
          const isPos = !!posLabel && a === posLabel;
          const isNeg = !!negLabel && a === negLabel;
          const bg = isPos
            ? 'bg-emerald-600 text-white'
            : isNeg
              ? 'bg-orange-600 text-white'
              : 'bg-slate-500 text-white';
          return (
            <span className={`inline-block rounded px-1.5 py-0.5 font-semibold ${bg}`}>{a}</span>
          );
        },
      });
      continue;
    }

    // *_code とその次の *_name を 1 セルに集約 (同じ prefix の場合)
    if (f.kind === 'text' && f.key.endsWith('_code')) {
      const prefix = f.key.slice(0, -'_code'.length);
      const nameField = itemFields.find((x) => x.key === `${prefix}_name`);
      if (nameField) {
        skip.add(nameField.key);
        // label の決定: コード列の自動 label は "${ax.label} コード" 形式 → ax.label に戻す
        const codeLabel = f.label || prefix;
        const groupLabel = codeLabel.replace(/\s*コード$/, '') || prefix;
        columns.push({
          key: prefix,
          label: groupLabel,
          render: (row) => (
            <>
              <div className="font-mono text-[10px] text-slate-500">{String(row[f.key] ?? '')}</div>
              <div className="text-slate-900">{String(row[nameField.key] ?? '')}</div>
            </>
          ),
        });
        continue;
      }
    }

    // 標準描画
    columns.push({
      key: f.key,
      label: f.label || f.key,
      render: (row, rowIndex) => renderCell(f, row, rowIndex),
    });
  }

  return columns;
}

function renderCell(
  field: AiOutputFieldDef,
  row: Record<string, unknown>,
  rowIndex: number,
): ReactNode {
  const raw = row[field.key];
  switch (field.kind) {
    case 'text': {
      // rank だけは "1" のような数字で短く表示
      if (field.key === 'rank') {
        const n = Number(raw);
        return <span className="font-mono">{Number.isFinite(n) ? n : rowIndex + 1}</span>;
      }
      const s = String(raw ?? '');
      return s ? (
        <span className="text-slate-700">{s}</span>
      ) : (
        <span className="text-slate-400">—</span>
      );
    }
    case 'enum-array': {
      const arr = Array.isArray(raw) ? (raw as unknown[]).map((x) => String(x)) : [];
      if (arr.length === 0) return <span className="text-slate-400">—</span>;
      return (
        <div className="flex flex-wrap gap-0.5">
          {arr.map((s) => (
            <span
              key={s}
              className="px-1 py-0.5 bg-slate-200 text-slate-700 rounded text-[10px] font-mono"
            >
              {s}
            </span>
          ))}
        </div>
      );
    }
    case 'code-list-string': {
      const s = String(raw ?? '');
      return s ? (
        <span className="font-mono text-xs">{s}</span>
      ) : (
        <span className="text-slate-400">—</span>
      );
    }
    case 'object-array':
      return <span className="text-slate-400">[object]</span>;
  }
}

/**
 * 行の背景色を assessment 値から決定 (ポジティブ→緑、ネガティブ→橙、それ以外→なし)。
 * old2 踏襲 (v2 preset の emerald/orange tone)。
 */
export function classificationRowBgClass(
  schema: AiOutputFormatSchema,
  row: Record<string, unknown>,
): string {
  const a = String(row.assessment ?? '');
  if (a && a === schema.assessment?.positiveLabel) return 'bg-emerald-50/60';
  if (a && a === schema.assessment?.negativeLabel) return 'bg-orange-50/60';
  return '';
}
